import type { FeatureCollection, MultiPolygon, Polygon, Position } from 'geojson';

export interface City {
  id: number;
  name: string;
  lat: number;
  lng: number;
  nogo: MultiPolygon;
  operative_area: MultiPolygon;
  nogo_lv: null;
  relocation: Polygon | null;
  parking_area: null;
  noparking: FeatureCollection<Polygon, []>;
  allow_parking: boolean;
  parking: Position[];
  parking_url: string | null;
  is_active: number;
  extension_price: number;
  booking_time: number;
  pause_price: number;
  waiting_time: number;
  relocation_time: number;
  preparation_time: number;
  pitstop_time: number;
  pitzone_time: number;
  minimum_cancel: number;
  credits_welcome: number;
  credits_referee: number;
  credits_referrer: number;
  credits_women: number | null;
  open_hour: number;
  close_hour: number;
  chat_closing_delay_min: number;
  country: string;
  show_items_size: boolean;
  check_insurance: number;
  min_ride_duration: number | null;
  min_radar_activation: number;
  min_book_distance: number | null;
  walker_discount: boolean | number | null;
  best_rate: number;
  packages: {
    promo: Package[];
    standard: Package[];
  };
  plans: Plan[];
  image: string;
  terms_link: string;
  terms_link_fr: string;
  terms_link_es: string;
  terms_link_en: string;
  terms_link_ca: string;
  privacy_policy_fr: string;
  privacy_policy_es: string;
  privacy_policy_en: string;
  privacy_policy_ca: string;
  terms_version: number;
  mandatory_license: boolean;
  driving_lesson_booking: string | null;
  vehicles: Vehicle[];
}

export interface Package {
  id: number;
  created_at: string | null;
  updated_at: string | null;
  name: string;
  credits: number | null;
  active: number;
  colors: string | null;
  image: string;
  price: number;
  promo: number;
}

interface Plan {
  id: number;
  created_at: string | null;
  updated_at: string | null;
  name: string;
  active: number;
  duration: number;
  daily_minutes: number;
  image: string;
  reset_cycle: string;
  price: number;
  minutes: number;
}

interface Vehicle {
  numbers: number;
  type: number;
  seat: number;
  price: number;
  speed: number;
  autonomy: number;
  type_name: string;
}

type VehicleMarker = {
  id: number;
  name: string;
  lat: number;
  lng: number;
  battery: number;
  type: number;
};

export interface Collection {
  id: string;
  workspace_id: string;
  name: string;
  default_locale: 'en';
  url: string;
  order: number;
  created_at: number;
  updated_at: number;
  slug: string;
  translated_content: {
    type: 'group_translated_content';
    en: CollectionLocale;
    ca?: CollectionLocale;
    fr?: CollectionLocale;
    es?: CollectionLocale;
  };
  description: string;
  icon: 'folder';
  parent_id: null | string;
  help_center_id: number;
  articles: Article[];
}

interface CollectionLocale {
  type: 'group_content';
  name: null | string;
  description: string | null;
}

export interface Article {
  id: string;
  type: 'article';
  workspace_id: string;
  parent_id: number | null;
  parent_type: 'collection' | null;
  parent_ids: number[];
  default_locale: 'en';
  slug: string;
  translated_content: {
    type: 'article_translated_content';
    ca: ArticleLocale;
    en: ArticleLocale;
    es: ArticleLocale;
    fr: ArticleLocale;
  };
  tags: {
    type: 'tag.list';
    tags: unknown[];
  };
  title: string;
  description: string;
  body: string;
  author_id: number;
  state: 'draft' | 'published';
  created_at: number;
  updated_at: number;
  url: null | string;
}

interface ArticleLocale {
  title: string;
  description: null | string;
  body: string;
  author_id: number;
  state: 'draft' | 'published';
  created_at: number;
  updated_at: number;
  url: null | string;
  type: 'article_content';
}

export const getData = async (): Promise<City[]> => {
  const response = await fetch(
    'https://yugo-assets.s3-eu-west-3.amazonaws.com/landing/data/data.json',
  );

  if (!response.ok) {
    throw new Error(`Error: ${response.statusText}`);
  }

  const data = await response.json();

  return data.data.cities as City[];
};

export const getMarkers = async (id: number): Promise<VehicleMarker[]> => {
  const response = await fetch(
    `https://yugo-assets.s3-eu-west-3.amazonaws.com/landing/data/markers-city-${id}.json`,
  );

  if (!response.ok) {
    throw new Error(`Error: ${response.statusText}`);
  }

  return response.json();
};

export const getFaqData = async (): Promise<Collection[]> => {
  const response = await fetch(
    'https://yugo-assets.s3.eu-west-3.amazonaws.com/landing/data/faq.json',
  );

  if (!response.ok) {
    throw new Error(`Error: ${response.statusText}`);
  }

  const data = await response.json();

  return data;
};
